import {
  DndContext,
  DragOverlay,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
  type DragOverEvent,
} from "@dnd-kit/core";
import { useCallback, useEffect, useState } from "react";

import useTranslations from "@/shared/lang/useTranslations";

import type { Payment, PaymentStatus } from "../types/payments.types";
import { canReleasePayment, isForceRelease } from "../utils/canReleasePayment";
import { CancelPaymentModal } from "./CancelPaymentModal";
import { ConfirmPaidModal } from "./ConfirmPaidModal";
import { ConfirmReleaseModal } from "./ConfirmReleaseModal";
import { ConfirmTakeModal } from "./ConfirmTakeModal";
import { PaymentCard } from "./PaymentCard";
import { PaymentCardPreview } from "./PaymentCardPreview";
import { PaymentColumn } from "./PaymentColumn";

const DROPPABLE_PENDING = "column-pending";
const DROPPABLE_IN_PROGRESS = "column-in_progress";
const DROPPABLE_PAID = "column-paid";

const DROPPABLE_IDS = [DROPPABLE_PENDING, DROPPABLE_IN_PROGRESS, DROPPABLE_PAID] as const;

function droppableIdToStatus(id: string): PaymentStatus | null {
  if (id === DROPPABLE_PENDING) return "pending";
  if (id === DROPPABLE_IN_PROGRESS) return "in_progress";
  if (id === DROPPABLE_PAID) return "paid";
  return null;
}

type PaymentsBoardProps = {
  pending?: Payment[];
  inProgress?: Payment[];
  paid?: Payment[];
  onStatusChange?: (
    payment: Payment,
    newStatus: PaymentStatus,
    attachments?: File[],
    urlHas?: string,
    paymentSource?: string,
  ) => void | Promise<void>;
  onCancel?: (id: string, reason?: string) => void;
  onTake?: (id: string) => void | Promise<void>;
  onRelease?: (id: string, force?: boolean) => void | Promise<void>;
  /** Used to decide who may release a payment: only its taker (or an admin). */
  currentUserId?: string;
  canForceRelease?: boolean;
  onViewHistory?: (id: string) => void;
  onViewPaymentInfo?: (id: string) => void;
  onNotifyClient?: (id: string) => void;
  onView?: (id: string, imageUrls?: string[] | null) => void;
  onTakerReminder?: (id: string) => void | Promise<void>;
  confirmPaidForPaymentId?: string | null;
  onConfirmPaidModalClose?: () => void;
  onRefresh?: () => void;
  isRefreshing?: boolean;
  cancelledPaymentIdFromSocket?: string | null;
  onConsumedCancelledPaymentId?: () => void;
};

const EMPTY_PAYMENTS: Payment[] = [];

export const PaymentsBoard = ({
  pending: controlledPending,
  inProgress: controlledInProgress,
  paid: controlledPaid,
  onStatusChange,
  onCancel,
  onTake,
  onRelease,
  currentUserId,
  canForceRelease = false,
  onViewHistory,
  onViewPaymentInfo,
  onNotifyClient,
  onView,
  onTakerReminder,
  confirmPaidForPaymentId,
  onConfirmPaidModalClose,
  onRefresh,
  isRefreshing = false,
  cancelledPaymentIdFromSocket,
  onConsumedCancelledPaymentId,
}: PaymentsBoardProps) => {
  const { t } = useTranslations("payments");

  const [internalPending, setInternalPending] = useState<Payment[]>(EMPTY_PAYMENTS);
  const [internalInProgress, setInternalInProgress] = useState<Payment[]>(EMPTY_PAYMENTS);
  const [internalPaid, setInternalPaid] = useState<Payment[]>(EMPTY_PAYMENTS);

  const pending = controlledPending ?? internalPending;
  const inProgress = controlledInProgress ?? internalInProgress;
  const paid = controlledPaid ?? internalPaid;

  const [activeId, setActiveId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);
  const [cancelTargetId, setCancelTargetId] = useState<string | null>(null);
  const [isCancelling, setIsCancelling] = useState(false);
  const [takeTargetId, setTakeTargetId] = useState<string | null>(null);
  const [isTaking, setIsTaking] = useState(false);
  const [paidTargetId, setPaidTargetId] = useState<string | null>(null);
  const [isMarkingPaid, setIsMarkingPaid] = useState(false);
  const [releaseTargetId, setReleaseTargetId] = useState<string | null>(null);
  const [isReleasing, setIsReleasing] = useState(false);

  useEffect(() => {
    if (confirmPaidForPaymentId != null && confirmPaidForPaymentId !== "") {
      setPaidTargetId(confirmPaidForPaymentId);
    }
  }, [confirmPaidForPaymentId]);

  useEffect(() => {
    if (cancelledPaymentIdFromSocket && cancelTargetId === cancelledPaymentIdFromSocket) {
      setCancelTargetId(null);
      onConsumedCancelledPaymentId?.();
    }
  }, [cancelledPaymentIdFromSocket, cancelTargetId, onConsumedCancelledPaymentId]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
    useSensor(KeyboardSensor),
  );

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(String(event.active.id));
  }, []);

  const handleDragOver = useCallback((event: DragOverEvent) => {
    setOverId(event.over ? String(event.over.id) : null);
  }, []);

  const allItems = useCallback(() => [...pending, ...inProgress, ...paid], [pending, inProgress, paid]);

  /** Only the holder of the lock (or an admin) may hand a payment back to the queue. */
  const canReleaseItem = useCallback(
    (item: Payment) => Boolean(onRelease) && canReleasePayment(item, currentUserId, canForceRelease),
    [onRelease, currentUserId, canForceRelease],
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      if (!over || !DROPPABLE_IDS.includes(String(over.id) as (typeof DROPPABLE_IDS)[number])) {
        setActiveId(null);
        setOverId(null);
        return;
      }

      const targetStatus = droppableIdToStatus(String(over.id));
      if (!targetStatus) {
        setActiveId(null);
        setOverId(null);
        return;
      }

      const id = String(active.id);
      const items = allItems();
      const item = items.find((p) => p.id === id);
      if (!item || item.status === targetStatus) {
        setActiveId(null);
        setOverId(null);
        return;
      }

      if (targetStatus === "in_progress" && item.status === "pending") {
        setActiveId(null);
        setOverId(null);
        setTakeTargetId(id);
        return;
      }

      // Paying is only reachable from En proceso — the backend has no PENDING → PAID
      // transition, so opening the modal from Pendientes would always end in an error.
      if (targetStatus === "paid") {
        setActiveId(null);
        setOverId(null);
        if (item.status === "in_progress") setPaidTargetId(id);
        return;
      }

      // Dragging back to Pendiente is a release, not a status change: the backend does not
      // allow IN_PROGRESS → PENDING through the status endpoint, and the lock must be
      // cleared too. Confirmed through the modal like every other transition, and ignored
      // when the dragger does not hold the payment.
      if (targetStatus === "pending" && item.status === "in_progress") {
        setActiveId(null);
        setOverId(null);
        if (canReleaseItem(item)) setReleaseTargetId(id);
        return;
      }

      const updated = { ...item, status: targetStatus };

      if (controlledPending === undefined) {
        const removeFrom = (list: Payment[]) => list.filter((p) => p.id !== id);
        const addToPending = targetStatus === "pending" ? [updated] : [];
        const addToInProgress = targetStatus === "in_progress" ? [updated] : [];
        setInternalPending((prev) => [...removeFrom(prev), ...addToPending]);
        setInternalInProgress((prev) => [...removeFrom(prev), ...addToInProgress]);
        setInternalPaid((prev) => removeFrom(prev));
      }

      onStatusChange?.(item, targetStatus);
      setActiveId(null);
      setOverId(null);
    },
    [controlledPending, allItems, onStatusChange, canReleaseItem],
  );

  const isControlled =
    controlledPending !== undefined || controlledInProgress !== undefined || controlledPaid !== undefined;

  const handleDragEndControlled = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event;

      if (!over || !DROPPABLE_IDS.includes(String(over.id) as (typeof DROPPABLE_IDS)[number])) {
        setActiveId(null);
        setOverId(null);
        return;
      }

      const targetStatus = droppableIdToStatus(String(over.id));
      if (!targetStatus) {
        setActiveId(null);
        setOverId(null);
        return;
      }

      const id = String(active.id);
      const items = allItems();
      const item = items.find((p) => p.id === id);
      if (!item || item.status === targetStatus) {
        setActiveId(null);
        setOverId(null);
        return;
      }

      if (targetStatus === "in_progress" && item.status === "pending") {
        setActiveId(null);
        setOverId(null);
        setTakeTargetId(id);
        return;
      }

      // Paying is only reachable from En proceso — the backend has no PENDING → PAID
      // transition, so opening the modal from Pendientes would always end in an error.
      if (targetStatus === "paid") {
        setActiveId(null);
        setOverId(null);
        if (item.status === "in_progress") setPaidTargetId(id);
        return;
      }

      // Same as above: back to Pendiente means releasing the payment, not a status change.
      if (targetStatus === "pending" && item.status === "in_progress") {
        setActiveId(null);
        setOverId(null);
        if (canReleaseItem(item)) setReleaseTargetId(id);
        return;
      }

      await onStatusChange?.(item, targetStatus);
      setActiveId(null);
      setOverId(null);
    },
    [allItems, onStatusChange, canReleaseItem],
  );

  const onDragEndHandler = isControlled ? handleDragEndControlled : handleDragEnd;

  const handleCancel = useCallback(
    async (id: string, reason?: string) => {
      if (controlledPending === undefined) {
        setInternalPending((prev) => prev.filter((p) => p.id !== id));
      }
      await onCancel?.(id, reason);
    },
    [controlledPending, onCancel],
  );

  const handleCancelClick = useCallback((id: string) => {
    setCancelTargetId(id);
  }, []);

  const handleCancelModalConfirm = useCallback(
    async (reason: string) => {
      if (!cancelTargetId) return;
      setIsCancelling(true);
      try {
        await Promise.resolve(handleCancel(cancelTargetId, reason));
        setCancelTargetId(null);
      } finally {
        setIsCancelling(false);
      }
    },
    [cancelTargetId, handleCancel],
  );

  const handleCancelModalClose = useCallback(() => {
    if (!isCancelling) setCancelTargetId(null);
  }, [isCancelling]);

  const handleTake = useCallback(
    async (id: string) => {
      const item = pending.find((p) => p.id === id);
      if (!item) return;
      if (controlledPending === undefined) {
        setInternalPending((prev) => prev.filter((p) => p.id !== id));
        setInternalInProgress((prev) => [...prev, { ...item, status: "in_progress" as const }]);
      }
      await onTake?.(id);
    },
    [pending, controlledPending, onTake],
  );

  const handleTakeClick = useCallback((id: string) => {
    setTakeTargetId(id);
  }, []);

  const handleTakeModalConfirm = useCallback(async () => {
    if (!takeTargetId) return;
    setIsTaking(true);
    try {
      await Promise.resolve(handleTake(takeTargetId));
      setTakeTargetId(null);
      setActiveId(null);
    } finally {
      setIsTaking(false);
    }
  }, [takeTargetId, handleTake]);

  const handleTakeModalClose = useCallback(() => {
    if (!isTaking) {
      setTakeTargetId(null);
      setActiveId(null);
    }
  }, [isTaking]);

  // Only a payment in progress can be paid: the backend rejects PENDING → PAID, so
  // offering it from Pendientes just wasted the operator's time filling the modal.
  const handleMarkAsPaid = useCallback(
    async (id: string, attachments?: File[], urlHas?: string, paymentSource?: string) => {
      const item = inProgress.find((p) => p.id === id);
      if (!item) return;
      const updated = { ...item, status: "paid" as const };
      if (controlledPending === undefined) {
        setInternalInProgress((prev) => prev.filter((p) => p.id !== id));
        setInternalPaid((prev) => [...prev.filter((p) => p.id !== id), updated]);
      }
      await onStatusChange?.(item, "paid", attachments, urlHas, paymentSource);
    },
    [inProgress, controlledPending, onStatusChange],
  );

  const handlePaidModalClose = useCallback(() => {
    if (!isMarkingPaid) {
      setPaidTargetId(null);
      setActiveId(null);
      onConfirmPaidModalClose?.();
    }
  }, [isMarkingPaid, onConfirmPaidModalClose]);

  const handlePaidModalConfirm = useCallback(
    async (attachments?: File[], urlHas?: string, paymentSource?: string) => {
      if (!paidTargetId || isMarkingPaid) return;
      setIsMarkingPaid(true);
      try {
        await Promise.resolve(handleMarkAsPaid(paidTargetId, attachments, urlHas, paymentSource));
        setPaidTargetId(null);
        setActiveId(null);
        onConfirmPaidModalClose?.();
      } finally {
        setIsMarkingPaid(false);
      }
    },
    [paidTargetId, isMarkingPaid, handleMarkAsPaid, onConfirmPaidModalClose],
  );

  const handleReleaseClick = useCallback((id: string) => {
    setReleaseTargetId(id);
  }, []);

  const handleReleaseModalClose = useCallback(() => {
    if (!isReleasing) setReleaseTargetId(null);
  }, [isReleasing]);

  const handleReleaseModalConfirm = useCallback(async () => {
    if (!releaseTargetId || isReleasing) return;
    const item = allItems().find((p) => p.id === releaseTargetId);
    // Recomputed here instead of when the modal opened: if the lock changed underneath us
    // (socket update), we still hit the endpoint that matches the current holder.
    const force = item ? isForceRelease(item, currentUserId) : false;
    setIsReleasing(true);
    try {
      await onRelease?.(releaseTargetId, force);
      setReleaseTargetId(null);
    } finally {
      setIsReleasing(false);
    }
  }, [releaseTargetId, isReleasing, allItems, onRelease, currentUserId]);

  const isDropTargetPending = overId === DROPPABLE_PENDING;
  const isDropTargetInProgress = overId === DROPPABLE_IN_PROGRESS;
  const isDropTargetPaid = overId === DROPPABLE_PAID;

  const activePayment = activeId ? (allItems().find((p) => p.id === activeId) ?? null) : null;

  const paidTargetPayment = paidTargetId ? (allItems().find((p) => p.id === paidTargetId) ?? null) : null;

  const releaseTargetPayment = releaseTargetId
    ? (allItems().find((p) => p.id === releaseTargetId) ?? null)
    : null;
  const releaseIsForce = releaseTargetPayment ? isForceRelease(releaseTargetPayment, currentUserId) : false;

  return (
    <DndContext
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={onDragEndHandler}
    >
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 h-full">
        <PaymentColumn
          columnId="pending"
          title={t("column_pending")}
          count={pending.length}
          variant="pending"
          droppableId={DROPPABLE_PENDING}
          isDropTarget={isDropTargetPending}
          emptyMessage={t("column_empty_pending")}
          onRefresh={onRefresh}
          isRefreshing={isRefreshing}
        >
          {pending.map((p) => (
            <PaymentCard
              key={p.id}
              payment={p}
              hidden={takeTargetId === p.id || paidTargetId === p.id || releaseTargetId === p.id}
              onCancel={handleCancelClick}
              onTake={handleTakeClick}
              onRelease={handleReleaseClick}
              currentUserId={currentUserId}
              canForceRelease={canForceRelease}
              onViewHistory={onViewHistory}
              onView={onView}
              onTakerReminder={onTakerReminder}
            />
          ))}
        </PaymentColumn>

        <PaymentColumn
          columnId="in_progress"
          title={t("column_in_progress")}
          count={inProgress.length}
          variant="in_progress"
          droppableId={DROPPABLE_IN_PROGRESS}
          isDropTarget={isDropTargetInProgress}
          onRefresh={onRefresh}
          isRefreshing={isRefreshing}
        >
          {inProgress.map((p) => (
            <PaymentCard
              key={p.id}
              payment={p}
              hidden={paidTargetId === p.id || releaseTargetId === p.id}
              onCancel={handleCancelClick}
              onRelease={handleReleaseClick}
              currentUserId={currentUserId}
              canForceRelease={canForceRelease}
              onViewHistory={onViewHistory}
              onViewPaymentInfo={onViewPaymentInfo}
              onView={onView}
              onTakerReminder={onTakerReminder}
            />
          ))}
        </PaymentColumn>

        <PaymentColumn
          columnId="paid"
          title={t("column_paid")}
          count={paid.length}
          variant="paid"
          droppableId={DROPPABLE_PAID}
          isDropTarget={isDropTargetPaid}
          onRefresh={onRefresh}
          isRefreshing={isRefreshing}
        >
          {paid.map((p) => (
            <PaymentCard
              key={p.id}
              payment={p}
              onViewHistory={onViewHistory}
              onNotifyClient={onNotifyClient}
              onView={onView}
              onTakerReminder={onTakerReminder}
            />
          ))}
        </PaymentColumn>
      </div>

      <DragOverlay dropAnimation={null}>
        {activePayment ? <PaymentCardPreview payment={activePayment} isOverlay /> : null}
      </DragOverlay>

      <CancelPaymentModal
        isOpen={cancelTargetId !== null}
        onClose={handleCancelModalClose}
        onConfirm={handleCancelModalConfirm}
        isLoading={isCancelling}
      />

      <ConfirmTakeModal
        isOpen={takeTargetId !== null}
        onClose={handleTakeModalClose}
        onConfirm={handleTakeModalConfirm}
        isLoading={isTaking}
      />

      <ConfirmPaidModal
        isOpen={paidTargetId !== null}
        onClose={handlePaidModalClose}
        onConfirm={handlePaidModalConfirm}
        isLoading={isMarkingPaid}
        requireComprobante
        currency={paidTargetPayment?.currency}
      />

      <ConfirmReleaseModal
        isOpen={releaseTargetId !== null}
        onClose={handleReleaseModalClose}
        onConfirm={handleReleaseModalConfirm}
        isLoading={isReleasing}
        isForce={releaseIsForce}
        takerName={releaseTargetPayment?.takenByName}
        takenAt={releaseTargetPayment?.takenAt}
      />
    </DndContext>
  );
};
